import {
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import { shouldTriggerOnboarding } from "../Utils/helper";
import Dialog from "../components/common/Dialog";
import OnboardingModal from "../components/Onboarding/OnboardingModal";

const SESSION_KEY_OPEN = "is_onboarding_open";
const SESSION_KEY_TARGET = "onboarding_target_meta";
const STORAGE_KEY_DONE = "has_seen_onboarding_v1";

export const useOnboardingNavigation = (questDetailsData, onboardingConfig) => {
  const dialogRef = useRef(null);
  const pendingClickTarget = useRef(null);
  const hasJustSeenRef = useRef(false);
  const [triggerReClick, setTriggerReClick] = useState(0);

  const questId = questDetailsData?.id;

  useLayoutEffect(() => {
    const openQuestId = sessionStorage.getItem(SESSION_KEY_OPEN);
    if (
      openQuestId &&
      openQuestId === questId &&
      shouldTriggerOnboarding(questDetailsData)
    ) {
      dialogRef.current?.open();
    }
  }, [questDetailsData, questId]);

  useEffect(() => {
    if (hasJustSeenRef.current) {
      const findTarget = () => {
        if (
          pendingClickTarget.current &&
          document.body.contains(pendingClickTarget.current)
        ) {
          return pendingClickTarget.current;
        }

        const metaStr = sessionStorage.getItem(SESSION_KEY_TARGET);
        if (metaStr) {
          try {
            const meta = JSON.parse(metaStr);

            if (meta.id) {
              const el = document.getElementById(meta.id);
              if (el) return el;
            }

            if (meta.tagName) {
              const candidates = document.getElementsByTagName(meta.tagName);
              for (let el of candidates) {
                const isClassMatch = meta.className
                  ? el.className === meta.className
                  : true;

                const currentText = (
                  el.innerText ||
                  el.textContent ||
                  ""
                ).trim();
                const isTextMatch = currentText === meta.text;

                if (isClassMatch && isTextMatch) {
                  return el;
                }
              }
            }
          } catch (e) {
            console.error("Failed to parse target meta", e);
          }
        }
        return null;
      };

      const target = findTarget();

      if (target) {
        setTimeout(() => {
          target.click();
          pendingClickTarget.current = null;
          sessionStorage.removeItem(SESSION_KEY_TARGET);
        }, 50);
      } else {
        console.warn("Could not auto-redirect: Target element lost.");
      }
    }
  }, [triggerReClick]);

  const handleInterceptInteraction = useCallback(
    (e) => {
      if (hasJustSeenRef.current) return;

      if (shouldTriggerOnboarding(questDetailsData)) {
        e.stopPropagation();
        e.preventDefault();

        const target =
          e.target.closest("button, a, [role='button'], .cursor-pointer") ||
          e.target;

        pendingClickTarget.current = target;

        const targetMeta = {
          id: target.id || null,
          tagName: target.tagName,
          className: target.className || "",
          text: (target.innerText || target.textContent || "").trim(),
        };
        sessionStorage.setItem(SESSION_KEY_TARGET, JSON.stringify(targetMeta));

        sessionStorage.setItem(SESSION_KEY_OPEN, questId);
        dialogRef.current?.open();
      }
    },
    [questDetailsData, questId],
  );

  const handleClose = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY_OPEN);
    localStorage.setItem(STORAGE_KEY_DONE, "true");

    dialogRef.current?.close();

    hasJustSeenRef.current = true;
    setTriggerReClick((prev) => prev + 1);
  }, []);

  const containerProps = useMemo(
    () => ({
      onClickCapture: handleInterceptInteraction,
    }),
    [handleInterceptInteraction],
  );

  const onboardingDialog = useMemo(
    () => (
      <Dialog
        ref={dialogRef}
        hiddenCloseButton={true}
        className="!p-0 !max-w-[340px] !rounded-[20px]"
      >
        <OnboardingModal onClose={handleClose} config={onboardingConfig} />
      </Dialog>
    ),
    [handleClose, onboardingConfig],
  );

  return {
    containerProps,
    onboardingDialog,
  };
};
